import { useEffect, useState } from 'react';
import { Play, Clock, Sparkles, AlertCircle } from 'lucide-react';
import SuccessStoryModal from './SuccessStoryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (!u ? null : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

// Story card — used for both the hero (large) and supporting tiles.
const StoryCard = ({ story, size = 'tile', onOpen }) => {
  const poster = absoluteUrl(story.thumbnail_url || story.poster_url);
  const isHero = size === 'hero';

  return (
    <button
      onClick={() => onOpen(story)}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900 text-left transition-all hover:scale-[1.01] hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
        isHero ? 'h-[420px] md:h-[480px] w-full' : 'h-56 md:h-60 w-full'
      }`}
      data-testid={`success-story-card-${story.slot}`}
    >
      {/* Background */}
      {poster ? (
        <img
          src={poster}
          alt={story.title}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${story.accent || 'from-slate-700 to-slate-900'}`} />
      )}

      {/* Cinematic dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/20" />

      {/* "Coming soon" ribbon when no video uploaded */}
      {!story.has_video && (
        <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-amber-400/95 text-amber-950 text-[10px] font-bold tracking-wider uppercase shadow-lg flex items-center gap-1" data-testid={`success-story-comingsoon-${story.slot}`}>
          <Sparkles className="w-3 h-3" />
          Coming Soon
        </div>
      )}

      {/* Duration badge */}
      {story.duration_label && (
        <div className="absolute top-3 left-3 z-10 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-white/85 text-[10px] font-semibold border border-white/10 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {story.duration_label}
        </div>
      )}

      {/* Centered play button */}
      <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${isHero ? '-mt-6' : ''}`}>
        <div className={`relative ${isHero ? 'w-20 h-20' : 'w-14 h-14'} rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300`}>
          <Play className={`${isHero ? 'w-9 h-9' : 'w-6 h-6'} text-slate-900 fill-current ml-1`} />
          <div className="absolute inset-0 rounded-full border-2 border-white/30 group-hover:border-white/60 scale-125 group-hover:scale-150 transition-all duration-500" />
        </div>
      </div>

      {/* Bottom text block */}
      <div className={`absolute bottom-0 inset-x-0 z-10 p-5 ${isHero ? 'md:p-7' : ''}`}>
        {isHero && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold tracking-widest uppercase mb-3">
            <Sparkles className="w-3 h-3" />
            Featured Story
          </div>
        )}
        <div className={`font-bold font-['Outfit'] text-white leading-tight ${isHero ? 'text-2xl md:text-3xl' : 'text-base md:text-lg'}`}>
          {story.title}
        </div>
        <div className={`text-white/85 italic ${isHero ? 'text-base md:text-lg mt-2' : 'text-xs md:text-sm mt-1'} leading-snug line-clamp-2`}>
          &ldquo;{story.tagline}&rdquo;
        </div>
        {isHero && (
          <div className="text-white/65 text-sm mt-3 max-w-2xl leading-relaxed line-clamp-2">
            {story.description}
          </div>
        )}
      </div>
    </button>
  );
};

export default function SuccessStoriesSection() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStory, setActiveStory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/success-stories`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setStories(d.stories || []); })
      .catch(() => { if (!cancelled) setError('Could not load Success Stories'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const hero = stories.find((s) => s.slot === 'hero') || stories[0];
  const supporting = stories.filter((s) => s.slot !== (hero?.slot || ''));

  return (
    <section
      className="py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden"
      data-testid="success-stories-section"
    >
      {/* Subtle grid texture for cinematic feel */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-bold tracking-widest uppercase mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Success Stories
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight font-['Outfit'] text-white mb-4">
            Real outcomes. <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Real growth.</span>
          </h2>
          <p className="text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
            See how professionals use ConnectMail to build referral partnerships,
            create marketing-ready signatures, and grow their book of business.
          </p>
        </div>

        {loading && (
          <div className="text-center text-white/50 text-sm py-10" data-testid="success-stories-loading">Loading…</div>
        )}

        {error && !loading && (
          <div className="text-center text-amber-200 text-sm py-10 flex items-center justify-center gap-2" data-testid="success-stories-error">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {!loading && !error && hero && (
          <div className="space-y-5">
            {/* Featured hero */}
            <StoryCard story={hero} size="hero" onOpen={setActiveStory} />

            {/* Four supporting tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {supporting.map((s) => (
                <StoryCard key={s.slot} story={s} size="tile" onOpen={setActiveStory} />
              ))}
            </div>
          </div>
        )}
      </div>

      {activeStory && (
        <SuccessStoryModal
          story={activeStory}
          onClose={() => setActiveStory(null)}
        />
      )}
    </section>
  );
}
